import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { globalSymbolsApi, symbolRequestsApi } from '@/api/services/globalSymbols';
import type {
  ApproveSymbolRequestDto,
  CreateGlobalSymbolDto,
  UpdateGlobalSymbolDto,
} from '@/api/types/globalSymbols';

export const globalSymbolKeys = {
  all: ['globalSymbols'] as const,
  list: ['globalSymbols', 'list'] as const,
  formData: ['globalSymbols', 'formData'] as const,
  history: ['symbolRequests', 'history'] as const,
  pending: ['symbolRequests', 'pending'] as const,
};

export function useFormData() {
  return useQuery({
    queryKey: globalSymbolKeys.formData,
    queryFn: () => globalSymbolsApi.getFormData(),
  });
}

export function useCreateGlobalSymbol() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateGlobalSymbolDto) => globalSymbolsApi.create(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: globalSymbolKeys.list }),
  });
}

export function useUpdateGlobalSymbol() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateGlobalSymbolDto }) =>
      globalSymbolsApi.update(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: globalSymbolKeys.list }),
  });
}

export function usePendingSymbolRequests(enabled: boolean) {
  return useQuery({
    queryKey: globalSymbolKeys.pending,
    queryFn: () => symbolRequestsApi.getPending(),
    enabled,
  });
}

export function useApproveSymbolRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ requestId, input }: { requestId: string; input: CreateGlobalSymbolDto }) =>
      symbolRequestsApi.approve(requestId, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: globalSymbolKeys.list });
      qc.invalidateQueries({ queryKey: globalSymbolKeys.history });
    },
  });
}

export function useApproveAllSymbolRequests() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (requests: ApproveSymbolRequestDto[]) => symbolRequestsApi.approveAll(requests),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: globalSymbolKeys.list });
      qc.invalidateQueries({ queryKey: globalSymbolKeys.history });
    },
  });
}

export function useRejectSymbolRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (requestId: string) => symbolRequestsApi.reject(requestId),
    onSuccess: () => qc.invalidateQueries({ queryKey: globalSymbolKeys.history }),
  });
}
